/* eslint-disable max-nested-callbacks */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  Modal,
  Input,
  INPUT_THEME,
  CheckBox,
  Button,
  BUTTON_THEME,
  Icon,
  Card,
  CARD_THEME,
} from '@st-operations/uikit/src/v2/index.js'

import { useEventFeedsContext } from '../../../context/index.jsx'
import { updateEventFeed } from '_services/data-explorer'
import { doesAttributeSupportAggregation } from '../../utils.js'
import { logErrorAsync } from '../../../../../lib/utility.js'
import { TYPE_PREBUILT_ATTRIBUTES_V2 } from '../../../wizard/index.jsx'

import './styles.scss'

const MODAL_STYLES = {
  content: 'modal-attribute__container',
  childWrapper: 'modal-attribute__wrapper',
  innerWrapper: 'modal-attribute__inner-wrapper',
  headerClose: 'modal-attribute__close-button',
}

// Format category to be display in the side menu e.g. from event_location to Event Location
const capitalizeCategories = (category) => {
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Remove unwanted keys
// eslint-disable-next-line no-unused-vars
const formatCategories = ({ default: defaultKey, custom, type, ...categories }) => {
  return { ...categories }
}

const ModalAddAttribute = ({ id, isOpen, onClose }) => {
  const { data, dispatch, ACTIONS, schema } = useEventFeedsContext()

  const predictors = data.type === TYPE_PREBUILT_ATTRIBUTES_V2

  const tagsScrollbar = useRef()

  const [attributes, setAttributes] = useState([])
  const [showDescription, setShowDescription] = useState(false)
  const [showOnlySelected, setOnlySelected] = useState(false)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [idCategorySelected, setIdCategorySelected] = useState('')
  const [listOfTagsOfEspecificAttribute, setListOfTagsOfEspecificAttribute] = useState([])
  const [currentAttributesList, setCurrentAttributesList] = useState(attributesList)
  const [selectedAttributeTag, setSelectedAttributeTag] = useState([])
  const [isSelectedAllAttributesAccordingTag, setIsSelectedAllAttributesAccordingTag] = useState(
    false
  )

  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollEnd, setScrollEnd] = useState(false)

  const [customAttributes, setCustomAttributes] = useState([])

  const buttonTag = document.querySelector('.modal-attribute__button-active-tag')

  useEffect(() => {
    if (data.customAttributes) {
      setCustomAttributes(data.customAttributes)
    }
  }, [data.customAttributes])

  const handleSubmit = async () => {
    const normalizedAttributes = attributes
      ?.map((category) => category.attributes?.filter((attribute) => attribute.selected))
      .flat()
      ?.map((attribute) => attribute.name)

    const newData = { ...data, attributes: normalizedAttributes, customAttributes }
    setIsLoading(true)

    try {
      const response = id !== 'discovery' ? await updateEventFeed(id, newData) : newData
      dispatch({
        type: ACTIONS.SET_DATA,
        payload: { ...response },
      })
      toast('Changes saved')
      onClose()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }

    setIsLoading(false)
  }

  const handleSearch = (value) => {
    setSearch(value)
    if (value) {
      setAttributes((prevState) =>
        prevState?.map((category) => {
          return {
            ...category,
            selected: false,
          }
        })
      )
    }
  }

  const handleSelectCategory = (event) => {
    const currentId = event.currentTarget.dataset.id
    setIdCategorySelected(currentId)
    setSearch('')
    setSelectedAttributeTag([])
    setAttributes((prevState) => {
      return prevState?.map((category) => {
        return {
          ...category,
          selected: category.id === currentId,
        }
      })
    })
  }

  const filterAttributes = (attributesList, selectedTag) =>
    attributesList
      ?.map((attribute) => {
        if (attribute.tags?.toString() === selectedTag) {
          return attribute
        }
      })
      ?.filter((element) => {
        return element !== undefined
      })

  const verifySelectedTagName = (selectedTag) => {
    if (listOfTagsOfEspecificAttribute.some((tag) => tag === selectedTag)) {
      return selectedTag
    }
    return selectedTag.replace(/[0-9]/g, '').trimEnd()
  }

  const handleFilterAccordingTag = (event) => {
    const verifiedSelectedTag = verifySelectedTagName(event.currentTarget.outerText)
    const tagAlreadySelected = selectedAttributeTag.some((tag) => tag === verifiedSelectedTag)
    const attributesListSelectedTag = filterAttributes(attributesList, verifiedSelectedTag)

    if (!selectedAttributeTag.length) {
      setSelectedAttributeTag([verifiedSelectedTag])
      return setCurrentAttributesList(attributesListSelectedTag)
    }
    if (selectedAttributeTag.length === 1 && tagAlreadySelected) {
      setSelectedAttributeTag([])
      return setCurrentAttributesList(attributesList)
    }

    if (selectedAttributeTag.length > 1 && tagAlreadySelected) {
      setSelectedAttributeTag((prevState) =>
        prevState.filter((state) => state !== verifiedSelectedTag)
      )
      return setCurrentAttributesList((prevState) =>
        prevState.filter((attribute) => attribute.tags?.toString() !== verifiedSelectedTag)
      )
    }

    setSelectedAttributeTag((prevState) => [...prevState, verifiedSelectedTag])
    return setCurrentAttributesList((prevState) => prevState.concat(attributesListSelectedTag))
  }

  const handleShowDescription = useCallback(() => {
    setShowDescription((prevState) => !prevState)
  }, [])

  const handleShowSelected = useCallback(() => {
    setOnlySelected((prevState) => !prevState)
  }, [])

  const handleSelectAttribute = ({ attribute, category }) => {
    setAttributes((prevState) => {
      return prevState?.map((currentCategory) => {
        return currentCategory.id === category
          ? {
              ...currentCategory,
              attributes: currentCategory.attributes?.map((currentAttribute) => {
                return currentAttribute.name === attribute
                  ? { ...currentAttribute, selected: !currentAttribute.selected }
                  : currentAttribute
              }),
            }
          : currentCategory
      })
    })
  }

  const selectedAttributes = useMemo(() => {
    return attributes?.reduce((acc, curr) => {
      return acc + curr?.attributes?.filter((attribute) => attribute.selected)?.length
    }, 0)
  }, [attributes])

  let attributesList = useMemo(() => {
    if (search) {
      const filteredList = attributes?.reduce((acc, curr) => {
        const filtered = curr.attributes?.filter((attribute) => {
          return (attribute.label || attribute.name).toLowerCase().includes(search.toLowerCase())
        })
        return [...acc, ...filtered]
      }, [])

      return showOnlySelected ? filteredList?.filter((item) => item.selected) : filteredList
    }
    return attributes
      ?.reduce((acc, curr) => {
        if (curr.selected) {
          return showOnlySelected
            ? curr.attributes?.filter((item) => item.selected)
            : curr.attributes
        }
        return acc
      }, [])
      ?.filter((item) => item.name?.includes(search))
  }, [attributes, showOnlySelected, search])

  useEffect(() => {
    if (!selectedAttributeTag.length) {
      setCurrentAttributesList(attributesList)
    } else {
      const arrAttributes = selectedAttributeTag?.flatMap((selectedTag) =>
        filterAttributes(attributesList, selectedTag)
      )

      setCurrentAttributesList(arrAttributes)
    }
  }, [attributesList, selectedAttributeTag])

  const categoriesList = useMemo(
    () =>
      attributes?.reduce((acc, curr) => {
        return [
          ...acc,
          {
            ...curr,
            indicator: curr.attributes?.reduce((acc, curr) => {
              if (curr.selected) {
                return acc + 1
              }
              return acc
            }, 0),
          },
        ]
      }, []),
    [attributes]
  )

  const handleAddAllAttributes = () => {
    setAttributes((prevState) => {
      return prevState?.map((currentCategory) => {
        return currentCategory.id === idCategorySelected
          ? isSelectedAllAttributesAccordingTag
            ? {
                ...currentCategory,
                attributes: currentCategory.attributes?.map((currentAttribute) => {
                  const shouldAdd = !!currentAttribute.tags?.filter((tag) =>
                    selectedAttributeTag.includes(tag)
                  ).length
                  if (shouldAdd || !selectedAttributeTag?.length) {
                    setIsSelectedAllAttributesAccordingTag(false)
                    return { ...currentAttribute, selected: false }
                  }
                  return { ...currentAttribute }
                }),
              }
            : {
                ...currentCategory,
                attributes: currentCategory.attributes?.map((currentAttribute) => {
                  const shouldAdd = !!currentAttribute.tags?.filter((tag) =>
                    selectedAttributeTag.includes(tag)
                  ).length
                  if (shouldAdd || !selectedAttributeTag?.length) {
                    setIsSelectedAllAttributesAccordingTag(true)
                    return { ...currentAttribute, selected: true }
                  }
                  return { ...currentAttribute }
                }),
              }
          : currentCategory
      })
    })
  }

  const isSelectedAllAttributes = useMemo(
    () => attributesList?.every((attribute) => attribute.selected),
    [attributesList, currentAttributesList]
  )

  const numberOfAttributesSelectedByTag = (tag) => {
    let counterAttributes = 0
    categoriesList.map((category) => {
      if (category.selected) {
        category.attributes?.map((attribute) => {
          if (attribute.tags?.toString() === tag && attribute.selected) {
            counterAttributes++
          }
        })
      }
    })
    return counterAttributes
  }

  useEffect(() => {
    const attributes = !predictors ? schema.attributes : schema.prebuilt_attributes_v2

    if (data.attributes && attributes) {
      const normalizedAttributes = Object.entries(formatCategories(attributes))?.map(
        ([key, value], index) => ({
          id: key,
          selected: !index ? true : false,
          name: capitalizeCategories(key),
          attributes: Object.entries(value)
            ?.map(([attributeKey, value]) => ({
              name: attributeKey,
              ...value,
              selected: data.attributes.includes(attributeKey),
              categoryRef: key,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        })
      )

      setAttributes(normalizedAttributes)
    }

    if (data.customAttributes) {
      setCustomAttributes(data.customAttributes)
    }
    setIdCategorySelected('Base')
  }, [data, schema, isOpen])

  useEffect(() => {
    if (idCategorySelected === 'Prebuilt B2B' || idCategorySelected === 'Prebuilt CPG') {
      let listOfTags = attributesList
        ?.map((attribute) => {
          return attribute.tags?.map((tag) => {
            return tag
          })
        })
        ?.map((element) => {
          return element.toString()
        })

      setListOfTagsOfEspecificAttribute(
        listOfTags?.filter((tag, index) => {
          return listOfTags.indexOf(tag) === index
        })
      )
    } else {
      setSelectedAttributeTag([])
    }
  }, [idCategorySelected, attributesList])

  useEffect(() => {
    setIsSelectedAllAttributesAccordingTag(
      currentAttributesList?.every((attribute) => attribute.selected)
    )
  }, [currentAttributesList, isSelectedAllAttributesAccordingTag])

  const slide = (shift) => {
    if (buttonTag === null || shift === 0) {
      tagsScrollbar.current.scrollLeft += 150
      setScrollPosition(scrollPosition + 150)
    } else {
      tagsScrollbar.current.scrollLeft += shift
      setScrollPosition(scrollPosition + shift)
    }
    if (
      Math.floor(tagsScrollbar.current.scrollWidth - tagsScrollbar.current.scrollLeft) <=
      tagsScrollbar.current.offsetWidth
    ) {
      setScrollEnd(true)
    } else {
      setScrollEnd(false)
    }
  }

  const scrollCheck = () => {
    setScrollPosition(tagsScrollbar.current.scrollLeft)
    if (
      Math.floor(tagsScrollbar.current.scrollWidth - tagsScrollbar.current.scrollLeft) <=
      tagsScrollbar.current.offsetWidth
    ) {
      setScrollEnd(true)
    } else {
      setScrollEnd(false)
    }
  }

  useEffect(() => {
    if (
      tagsScrollbar.current &&
      tagsScrollbar?.current?.scrollWidth === tagsScrollbar?.current?.offsetWidth
    ) {
      setScrollEnd(true)
    } else {
      setScrollEnd(false)
    }
    return () => {}
  }, [tagsScrollbar?.current?.scrollWidth, tagsScrollbar?.current?.offsetWidth])

  return (
    <Modal isOpen={isOpen} onClose={onClose} classes={MODAL_STYLES}>
      <section className="modal-attribute__content">
        <header className="modal-attribute__header">
          <h1 className="modal-attribute__header-title">Add Attributes</h1>
          <Input
            theme={INPUT_THEME.SEARCH}
            placeholder="Quick Search"
            className="modal-attribute__header-search-input"
            classes={{ inputContainer: 'modal-attribute__header-search' }}
            value={search}
            onChange={handleSearch}
          />
        </header>
        <section className="modal-attribute__main-section">
          <aside className="modal-attribute__menu">
            <For each="category" of={categoriesList}>
              <Button
                theme={BUTTON_THEME.GHOST}
                className={classnames(
                  { 'modal-attribute__menu-selected-category': category?.selected },
                  'modal-attribute__menu-category'
                )}
                key={category?.id}
                onClick={handleSelectCategory}
                data-id={category?.id}
              >
                {category?.name}
                <If condition={category?.indicator}>
                  <div className="indicator">{category?.indicator}</div>
                </If>
              </Button>
            </For>
          </aside>
          <div className="modal-attribute__attributes">
            <div className="modal-attribute__attributes-list">
              <If condition={attributesList?.length}>
                <>
                  <If
                    condition={
                      idCategorySelected === 'Prebuilt B2B' || idCategorySelected === 'Prebuilt CPG'
                    }
                  >
                    <div className="modal-attribute__scrollbar-group-of-tags">
                      <Button
                        className={classnames('modal-attribute__button-to-scroll', {
                          'modal-attribute__shadow-right': scrollPosition > 0,
                        })}
                        onClick={() => slide(-buttonTag?.offsetWidth)}
                      >
                        <Icon
                          width="24px"
                          height="24px"
                          icon="angleleft"
                          type="outline"
                          color={scrollPosition > 0 ? '#13682B' : 'rgba(19, 104, 43, 0.4)'}
                        />
                      </Button>
                      <div
                        className="modal-attribute__group-buttons-to-filter"
                        ref={tagsScrollbar}
                        onScroll={scrollCheck}
                      >
                        <For each="tag" of={listOfTagsOfEspecificAttribute}>
                          <Button
                            className={classnames('modal-attribute__button-active-tag', {
                              'modal-attribute__button-active-tag--selected': selectedAttributeTag.includes(
                                tag
                              ),
                            })}
                            theme={BUTTON_THEME.GHOST}
                            key={tag}
                            onClick={handleFilterAccordingTag}
                          >
                            <div className="modal-attribute__button-active-tag--name-tag">
                              {tag}
                              <If condition={numberOfAttributesSelectedByTag(tag) !== 0}>
                                <div
                                  className={classnames(
                                    'modal-attribute__button-active-tag--count-attributes',
                                    {
                                      'modal-attribute__button-active-tag--count-selected-attributes': selectedAttributeTag.includes(
                                        tag
                                      ),
                                    }
                                  )}
                                >
                                  {numberOfAttributesSelectedByTag(tag)}
                                </div>
                              </If>
                            </div>
                          </Button>
                        </For>
                      </div>
                      <Button
                        className={classnames('modal-attribute__button-to-scroll', {
                          'modal-attribute__shadow-left': !scrollEnd,
                        })}
                        onClick={() => slide(+buttonTag?.offsetWidth)}
                      >
                        <Icon
                          width="24px"
                          height="24px"
                          icon="angleleft"
                          type="outline"
                          color={!scrollEnd ? '#13682B' : 'rgba(19, 104, 43, 0.4)'}
                          customStyle={{ transform: 'rotate(180deg)' }}
                        />
                      </Button>
                    </div>
                  </If>
                  <CheckBox
                    label="All Attributes"
                    isIndeterminate={isSelectedAllAttributes || isSelectedAllAttributesAccordingTag}
                    className="modal-attribute__attributes-all"
                    onChange={handleAddAllAttributes}
                    classes={{
                      root: 'modal-attribute__checkbox-size',
                      label: 'modal-attribute__selected-indeterminate',
                    }}
                  />
                  <For each="attribute" of={currentAttributesList}>
                    <Card
                      key={attribute.name + attribute?.categoryRef}
                      theme={CARD_THEME.NO_SHADOW}
                      className="modal-attribute__attributes-card"
                    >
                      <CheckBox
                        data-attribute={attribute.name}
                        data-category={attribute.categoryRef}
                        disabled={
                          data.aggregations?.length > 0
                            ? !doesAttributeSupportAggregation(attribute.name)
                            : false
                        }
                        label={attribute.label || attribute.name}
                        classes={{
                          root: `modal-attribute__checkbox-size ${
                            data.aggregations?.length > 0
                              ? !doesAttributeSupportAggregation(attribute.name) && 'disabled'
                              : ''
                          }`,
                          label: attribute.selected ? 'modal-attribute__selected-checkbox' : '',
                        }}
                        checked={attribute.selected}
                        onChange={(event) =>
                          handleSelectAttribute({
                            attribute: event.currentTarget.dataset.attribute,
                            category: event.currentTarget.dataset.category,
                          })
                        }
                      />
                      <If condition={showDescription && attribute.description}>
                        <p className="modal-attribute__attributes-card-description">
                          {attribute.description}
                        </p>
                      </If>
                    </Card>
                  </For>
                </>
              </If>
              <If condition={!attributesList?.length && search}>
                <span className={'modal-attribute__empty-message'}>
                  {"We couldn't find the attribute you're looking for. Please try again."}
                </span>
              </If>
            </div>
            <If condition={data.aggregations?.length > 0}>
              <div className="modal-attribute__aggregation-message">
                <Icon type="fill" icon="error" width="24px" height="24px" color="#F7DB71" />
                <p className="modal-attribute__aggregation-message--text">
                  Some attributes are not available for aggregating
                </p>
              </div>
            </If>
          </div>
        </section>

        <footer className="modal-attribute__footer">
          <span>{selectedAttributes} selected total</span>
          <div className="modal-attribute__footer-actions">
            <CheckBox
              checked={showDescription}
              onChange={handleShowDescription}
              label="Show Descriptions"
              classes={{
                root: 'modal-attribute__checkbox-size',
                label: showDescription
                  ? 'modal-attribute__footer-checked-checkbox'
                  : 'modal-attribute__footer-checkbox',
              }}
            />
            <CheckBox
              checked={showOnlySelected}
              onChange={handleShowSelected}
              label="Show Only Selected"
              classes={{
                root: 'modal-attribute__checkbox-size',
                label: showOnlySelected
                  ? 'modal-attribute__footer-checked-checkbox'
                  : 'modal-attribute__footer-checkbox',
              }}
            />
            <Button isLoading={isLoading} onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </footer>
      </section>
    </Modal>
  )
}

ModalAddAttribute.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
}

ModalAddAttribute.defaultProps = {
  id: '',
  isOpen: false,
}

export default React.memo(ModalAddAttribute)
